import { useEffect, useLayoutEffect, useRef, useState } from 'preact/hooks';
import {
  ArrowDown,
  ArrowLeft,
  ArrowRight,
  ArrowUp,
  CornerUpLeft,
  CornerUpRight,
  Minus,
  MoveUpLeft,
  MoveUpRight,
  RectangleHorizontal,
  Trash2,
} from 'lucide-preact';
import type { CoasterRuntime, RuntimeSnapshot } from '../app/runtime-types.ts';
import type { AuthoredBank, AuthoredPitch } from '../builder-v2/index.ts';
import type { TrackBehavior } from '../schema/index.ts';
import {
  BASIC_BANK_CHOICES,
  BASIC_DIRECTION_CHOICES,
  BASIC_SLOPE_CHOICES,
  SPECIAL_TRACK_GROUPS,
  basicChoiceAvailable,
  basicDisplayLabel,
  basicSelectionForDescriptor,
  contextualSpecialSuccessor,
  contextualSpecialTrackEntries,
  defaultBasicSelection,
  isSpecialTrackDescriptor,
  reconcileBasicRegularPiece,
  resolveBasicRegularPiece,
  selectionWithChoice,
  specialTrackGroup,
  type BasicBankId,
  type BasicDirectionId,
  type BasicRegularAxis,
  type BasicRegularChoice,
  type BasicSlopeId,
} from './basic-build-catalog.ts';
import { AuthoredPieceThumbnail } from './AuthoredPieceThumbnail.tsx';

interface BasicBuildProps {
  readonly state: RuntimeSnapshot;
  readonly runtime: CoasterRuntime | null;
}

const TRACK_TYPE_CHOICES = [
  ['normal', 'Normal'],
  ['lift', 'Lift'],
  ['launch', 'Launch'],
  ['brake', 'Brakes'],
  ['photo', 'Photo'],
] as const;

const TRACK_TYPE_PREVIEW_LABELS = {
  normal: 'Normal Track',
  lift: 'Lift Track',
  launch: 'Launch Track',
  brake: 'Brake Track',
  photo: 'Photo Track',
} as const;

export function isInspectingCommittedAuthoredPiece(state: RuntimeSnapshot): boolean {
  return (
    Boolean(state.selectedAuthoredPieceId) &&
    (state.topologyStatus === 'closed' || !state.selectedAuthoredPieceIsActiveEndpoint)
  );
}

export function ExistingAuthoredPieceEditor({
  state,
  runtime,
  compact = false,
  showDeleteAction = true,
}: BasicBuildProps & { readonly compact?: boolean; readonly showDeleteAction?: boolean }) {
  const currentType = state.selectedSectionBehavior?.kind ?? 'normal';
  const [pendingType, setPendingType] = useState<TrackBehavior['kind']>(currentType);
  const stationSelected = currentType === 'station';
  const trackTypeChoices = TRACK_TYPE_CHOICES.filter(([value]) =>
    state.selectedSectionAllowedBehaviors.includes(value),
  );

  useEffect(() => {
    setPendingType(currentType);
  }, [currentType, state.selectedAuthoredPieceId]);

  return (
    <section
      class={`existing-authored-piece-editor ${compact ? 'is-compact' : ''}`}
      aria-label="Edit selected track piece"
    >
      <div class="existing-piece-heading">
        <span>Selected Piece</span>
        <b>{state.selectedSectionLabel ?? 'Track Piece'}</b>
      </div>
      <div
        class={`selected-piece-navigation ${
          state.topologyStatus !== 'closed' ? 'has-track-end-return' : ''
        }`}
      >
        <button type="button" onClick={() => runtime?.navigateSelectedAuthoredPiece(-1)}>
          <ArrowLeft aria-hidden="true" size={17} />
          <span>Previous Piece</span>
        </button>
        <button type="button" onClick={() => runtime?.navigateSelectedAuthoredPiece(1)}>
          <span>Next Piece</span>
          <ArrowRight aria-hidden="true" size={17} />
        </button>
        {state.topologyStatus !== 'closed' && (
          <button type="button" onClick={() => runtime?.returnToAuthoredBuildEnd()}>
            {state.authoredGapCount > 0 ? 'Go to Repair Point' : 'Go to Track End'}
          </button>
        )}
      </div>

      {stationSelected ? (
        <small>The station is protected. Choose another piece to edit it.</small>
      ) : (
        <>
          <h4>Track Type</h4>
          <div
            class="segmented-control existing-track-type-control"
            role="group"
            aria-label="Track Type"
          >
            {TRACK_TYPE_CHOICES.map(([value, label]) => {
              const allowed = trackTypeChoices.some(([candidate]) => candidate === value);
              return (
                <button
                  key={value}
                  type="button"
                  aria-pressed={pendingType === value}
                  disabled={!allowed}
                  title={allowed ? `${label} track` : `This piece cannot use ${label}`}
                  onClick={() => setPendingType(value)}
                >
                  {label}
                </button>
              );
            })}
          </div>
          <button
            class="primary-action apply-track-type-action"
            type="button"
            disabled={pendingType === currentType}
            onClick={() => runtime?.setSectionBehavior(pendingType)}
          >
            Apply Track Type
          </button>
        </>
      )}

      {showDeleteAction && !stationSelected && (
        <button
          class="danger-action delete-selected-piece existing-piece-delete-action"
          type="button"
          disabled={!state.canDeleteSelectedPiece}
          title={state.selectedPieceDeleteBlockedReason ?? 'Delete this track piece'}
          onClick={() => runtime?.deleteSelectedAuthoredPiece()}
        >
          <Trash2 aria-hidden="true" size={17} />
          Delete Selected Piece
        </button>
      )}
      {state.selectedPieceDeleteBlockedReason && !stationSelected && (
        <small>{state.selectedPieceDeleteBlockedReason}</small>
      )}
    </section>
  );
}

function currentPitch(state: RuntimeSnapshot): AuthoredPitch {
  return state.buildEnd?.authoredPose?.pitchDegrees ?? 0;
}

function currentBank(state: RuntimeSnapshot): AuthoredBank {
  if (state.buildEnd?.authoredPose) return state.buildEnd.authoredPose.bank;
  const bank = state.buildEnd?.pose.bankDegrees ?? 0;
  return bank < 0 ? 'left' : bank > 0 ? 'right' : 'level';
}

function useReconciledBasicSelection(
  state: RuntimeSnapshot,
  runtime: CoasterRuntime | null,
  enabled = true,
) {
  const pitch = currentPitch(state);
  const bank = currentBank(state);
  const selectedId = state.authoredSelection.descriptorId;
  const requestedSelection =
    basicSelectionForDescriptor(selectedId, pitch, bank) ?? defaultBasicSelection(pitch, bank);
  const resolvedPiece = reconcileBasicRegularPiece(state.authoredCatalog, selectedId, pitch, bank);
  const selection =
    (resolvedPiece && basicSelectionForDescriptor(resolvedPiece.id, pitch, bank)) ??
    requestedSelection;
  const previewId = state.authoredCatalog.find((entry) => entry.selected)?.id ?? null;

  // Placing a transition changes the endpoint pose without changing the last
  // selected descriptor. Keep the user's still-valid intent, but advance the
  // runtime preview to the official piece that expresses it at the new pose.
  // Desktop and mobile must share this reconciliation: otherwise mobile can
  // show a highlighted Direction while its actionable descriptor is stale.
  useEffect(() => {
    if (
      enabled &&
      (!state.preview || state.preview.kind === 'piece') &&
      resolvedPiece &&
      resolvedPiece.id !== previewId
    ) {
      runtime?.selectAuthoredPiece(resolvedPiece.id, false);
    }
  }, [enabled, previewId, resolvedPiece?.id, runtime, state.preview?.kind]);

  return { pitch, bank, selection };
}

function useSelectedOptionScroller(selectedKey: string) {
  const scrollerRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    const scroller = scrollerRef.current;
    const selected = scroller?.querySelector<HTMLElement>('[aria-pressed="true"]');
    if (!scroller || !selected) return;

    const frame = window.requestAnimationFrame(() => {
      selected.scrollIntoView({
        block: 'nearest',
        inline: 'center',
        behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
      });
    });

    return () => window.cancelAnimationFrame(frame);
  }, [selectedKey]);

  return scrollerRef;
}

function TrackTypeControl({
  state,
  runtime,
  mobile = false,
}: BasicBuildProps & { mobile?: boolean }) {
  const selected = state.authoredCatalog.find(
    (entry) => entry.id === state.authoredSelection.descriptorId,
  );
  return (
    <div
      class={mobile ? 'mobile-track-type-control' : 'segmented-control track-type-control'}
      role="group"
      aria-label="Track Type"
      data-onboarding-target="track-type"
    >
      {TRACK_TYPE_CHOICES.map(([value, label]) => {
        const allowed = selected?.allowedTrackTypes.includes(value) ?? false;
        return (
          <button
            key={value}
            type="button"
            aria-pressed={state.authoredSelection.trackType === value}
            data-onboarding-target={
              value === 'photo'
                ? 'track-type-photo'
                : value === 'brake'
                  ? 'track-type-brake'
                  : undefined
            }
            disabled={!allowed}
            title={
              allowed ? `${label} track` : `${selected?.label ?? 'This piece'} cannot use ${label}`
            }
            onClick={() => runtime?.setAuthoredTrackType(value)}
          >
            {label}
          </button>
        );
      })}
    </div>
  );
}

function DirectionGlyph({ id }: { readonly id: BasicDirectionId }) {
  const props = { 'aria-hidden': true, size: 25, strokeWidth: 2.2 } as const;
  if (id === 'direction.left.small') return <CornerUpLeft {...props} />;
  if (id === 'direction.left.large') return <CornerUpLeft {...props} class="is-large-turn" />;
  if (id === 'direction.left.45') return <MoveUpLeft {...props} />;
  if (id === 'direction.right.45') return <MoveUpRight {...props} />;
  if (id === 'direction.right.large') return <CornerUpRight {...props} class="is-large-turn" />;
  if (id === 'direction.right.small') return <CornerUpRight {...props} />;
  return <ArrowUp {...props} />;
}

function SlopeGlyph({ id }: { readonly id: BasicSlopeId }) {
  if (id === 'slope.n90') return <ArrowDown aria-hidden="true" size={25} strokeWidth={2.2} />;
  if (id === 'slope.90') return <ArrowUp aria-hidden="true" size={25} strokeWidth={2.2} />;
  return (
    <span class={`basic-slope-glyph ${id.replace('.', '-')}`} aria-hidden="true">
      <Minus size={26} strokeWidth={2.4} />
      <Minus size={27} strokeWidth={1.4} />
    </span>
  );
}

function BankGlyph({ id }: { readonly id: BasicBankId }) {
  return (
    <span class={`basic-bank-glyph ${id.replace('.', '-')}`} aria-hidden="true">
      <RectangleHorizontal size={25} strokeWidth={2.1} />
      <Minus size={28} strokeWidth={1.8} />
    </span>
  );
}

function SelectedPieceCard({ state, runtime }: BasicBuildProps) {
  const selected = state.authoredCatalog.find(
    (entry) => entry.id === state.authoredSelection.descriptorId,
  );
  const placeReady = state.preview?.kind === 'piece' && state.preview.canAccept;
  const closed = state.topologyStatus === 'closed';
  return (
    <section class="basic-selected-result" aria-label="Selected official piece">
      <button
        class={`primary-action ${closed ? '' : 'place-piece-action'}`}
        type="button"
        data-audio-cue={closed ? undefined : 'track-place'}
        data-onboarding-target={closed ? undefined : 'place-piece'}
        disabled={closed || !placeReady}
        onClick={() => runtime?.acceptPreview()}
      >
        {closed ? 'Track Complete' : 'Place Piece'}
      </button>
      <h4>Selected Piece</h4>
      <div class="basic-selected-piece-preview">
        {selected && <AuthoredPieceThumbnail descriptorId={selected.id} priority />}
        {selected && selected.allowedTrackTypes.length > 1 && (
          <span
            class={`basic-selected-track-type is-${state.authoredSelection.trackType}`}
            aria-label={`Selected track type: ${TRACK_TYPE_PREVIEW_LABELS[state.authoredSelection.trackType]}`}
          >
            {TRACK_TYPE_PREVIEW_LABELS[state.authoredSelection.trackType]}
          </span>
        )}
      </div>
      <b>{selected ? basicDisplayLabel(selected.id, selected.label) : 'Choose a piece'}</b>
    </section>
  );
}

function RegularBuildOptions({ state, runtime }: BasicBuildProps) {
  const { pitch, bank, selection } = useReconciledBasicSelection(state, runtime);

  const choose = (axis: BasicRegularAxis, choice: BasicRegularChoice): void => {
    const target = resolveBasicRegularPiece(
      state.authoredCatalog,
      selectionWithChoice(selection, axis, choice),
      pitch,
      bank,
    );
    if (target) runtime?.selectAuthoredPiece(target.id, false);
  };
  const available = (axis: BasicRegularAxis, choice: BasicRegularChoice): boolean =>
    basicChoiceAvailable(state.authoredCatalog, selection, axis, choice, pitch, bank);
  const recommended = (axis: BasicRegularAxis, choice: BasicRegularChoice): boolean =>
    resolveBasicRegularPiece(
      state.authoredCatalog,
      selectionWithChoice(selection, axis, choice),
      pitch,
      bank,
    )?.recommendedRank === 1;

  return (
    <div class="basic-options-panel">
      <section class="basic-option-row basic-direction-row">
        <h4>Direction</h4>
        <div role="group" aria-label="Direction">
          {BASIC_DIRECTION_CHOICES.map((choice) => (
            <button
              key={choice.id}
              type="button"
              aria-label={choice.accessibleLabel}
              aria-pressed={selection.direction === choice.id}
              disabled={!available('direction', choice.id)}
              class={recommended('direction', choice.id) ? 'is-route-recommended' : ''}
              data-onboarding-target={
                choice.id === 'direction.left.large'
                  ? 'left-large-turn'
                  : choice.id === 'direction.straight'
                    ? 'direction-straight'
                    : undefined
              }
              onClick={() => choose('direction', choice.id)}
            >
              <DirectionGlyph id={choice.id} />
              <span>{choice.label}</span>
            </button>
          ))}
        </div>
      </section>

      <section class="basic-option-row basic-slope-row">
        <h4>Slope</h4>
        <div role="group" aria-label="Slope">
          {BASIC_SLOPE_CHOICES.map((choice) => (
            <button
              key={choice.id}
              type="button"
              aria-label={`${choice.pitch < 0 ? 'Down ' : choice.pitch > 0 ? 'Up ' : ''}${choice.label}`}
              aria-pressed={selection.slope === choice.id}
              disabled={!available('slope', choice.id)}
              class={recommended('slope', choice.id) ? 'is-route-recommended' : ''}
              data-onboarding-target={
                choice.id === 'slope.25'
                  ? 'uphill-slope'
                  : choice.id === 'slope.n25'
                    ? 'downhill-slope'
                    : choice.id === 'slope.0'
                      ? 'level-slope'
                      : undefined
              }
              onClick={() => choose('slope', choice.id)}
            >
              <SlopeGlyph id={choice.id} />
              <span>{choice.label}</span>
            </button>
          ))}
        </div>
      </section>

      <section class="basic-option-row basic-bank-row">
        <h4>Banking</h4>
        <div role="group" aria-label="Banking">
          {BASIC_BANK_CHOICES.map((choice) => (
            <button
              key={choice.id}
              type="button"
              aria-pressed={selection.bank === choice.id}
              disabled={!available('bank', choice.id)}
              class={recommended('bank', choice.id) ? 'is-route-recommended' : ''}
              data-onboarding-target={
                choice.id === 'bank.left'
                  ? 'bank-left'
                  : choice.id === 'bank.level'
                    ? 'bank-level'
                    : undefined
              }
              onClick={() => choose('bank', choice.id)}
            >
              <BankGlyph id={choice.id} />
              <span>{choice.label}</span>
            </button>
          ))}
        </div>
      </section>

      <section class="basic-option-row basic-track-type-row">
        <h4>Track Type</h4>
        <TrackTypeControl state={state} runtime={runtime} />
      </section>
    </div>
  );
}

export function BasicBuildComposer({ state, runtime }: BasicBuildProps) {
  const selectedId = state.authoredSelection.descriptorId;
  const [catalogMode, setCatalogMode] = useState<'regular' | 'special'>(
    isSpecialTrackDescriptor(selectedId) ? 'special' : 'regular',
  );
  const specialEntries = state.authoredCatalog.filter((entry) =>
    isSpecialTrackDescriptor(entry.id),
  );
  const contextualSpecials = contextualSpecialTrackEntries(specialEntries);
  const regularAvailableCount = state.authoredCatalog.filter(
    (entry) => entry.allowed && !isSpecialTrackDescriptor(entry.id) && entry.category !== 'station',
  ).length;
  const specialAvailableCount = contextualSpecials.filter(({ entry }) => entry.allowed).length;

  useEffect(() => {
    if (isSpecialTrackDescriptor(selectedId)) {
      setCatalogMode('special');
    }
  }, [selectedId]);

  useEffect(() => {
    if (state.preview && state.preview.kind !== 'piece') return;
    if (!isSpecialTrackDescriptor(selectedId)) return;
    const successor = contextualSpecialSuccessor(specialEntries, selectedId);
    const previewId = state.authoredCatalog.find((entry) => entry.selected)?.id ?? null;
    if (successor && successor.id !== previewId) {
      runtime?.selectAuthoredPiece(successor.id, false);
    }
  }, [runtime, selectedId, specialEntries, state.authoredCatalog, state.preview?.kind]);

  if (isInspectingCommittedAuthoredPiece(state)) {
    return (
      <div class="basic-build-composer" data-testid="basic-build-composer">
        <ExistingAuthoredPieceEditor state={state} runtime={runtime} />
      </div>
    );
  }

  if (state.topologyStatus === 'closed') {
    return (
      <div class="basic-build-composer" data-testid="basic-build-composer">
        <section class="completed-basic-build" role="status">
          <strong>Track Complete</strong>
          <small>Select any track piece to change its Track Type or reopen the route.</small>
          <div class="selected-piece-navigation completed-track-navigation">
            <button type="button" onClick={() => runtime?.navigateSelectedAuthoredPiece(-1)}>
              <ArrowLeft aria-hidden="true" size={17} />
              <span>Previous Piece</span>
            </button>
            <button type="button" onClick={() => runtime?.navigateSelectedAuthoredPiece(1)}>
              <span>Next Piece</span>
              <ArrowRight aria-hidden="true" size={17} />
            </button>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div
      class="basic-build-composer"
      data-testid="basic-build-composer"
      data-onboarding-target="basic-composer"
    >
      <div class="basic-catalog-mode" role="group" aria-label="Track piece catalog">
        <button
          type="button"
          aria-pressed={catalogMode === 'regular'}
          data-onboarding-target="regular-track-tab"
          onClick={() => {
            setCatalogMode('regular');
            runtime?.setCreatorGuideCatalogMode('regular');
          }}
        >
          Regular Track <span>{regularAvailableCount}</span>
        </button>
        <button
          type="button"
          aria-pressed={catalogMode === 'special'}
          data-onboarding-target="special-track-tab"
          onClick={() => {
            setCatalogMode('special');
            runtime?.setCreatorGuideCatalogMode('special');
          }}
        >
          Special Track <span>{specialAvailableCount}</span>
        </button>
      </div>

      {catalogMode === 'regular' ? (
        <div class="basic-intent-browser" data-testid="basic-intent-browser">
          <RegularBuildOptions state={state} runtime={runtime} />
          <SelectedPieceCard state={state} runtime={runtime} />
        </div>
      ) : (
        <div class="basic-special-layout">
          <div class="basic-special-browser" data-testid="basic-special-browser">
            {SPECIAL_TRACK_GROUPS.map((group) => {
              const entries = contextualSpecials.filter(
                ({ entry }) => specialTrackGroup(entry.id) === group.id,
              );
              if (!entries.length) return null;
              return (
                <section key={group.id}>
                  <h4>{group.label}</h4>
                  <div class="basic-special-grid">
                    {entries.map(({ entry, displayLabel }) => (
                      <button
                        key={entry.id}
                        type="button"
                        class={`${entry.selected ? 'is-previewing' : ''} ${
                          entry.recommendedRank === 1 ? 'is-route-recommended' : ''
                        }`}
                        aria-pressed={entry.selected}
                        data-onboarding-target={
                          entry.id === 'roll.left' ? 'barrel-roll-left' : undefined
                        }
                        disabled={!entry.allowed}
                        title={
                          entry.allowed ? entry.description : (entry.reason ?? 'Unavailable here')
                        }
                        onClick={() => runtime?.selectAuthoredPiece(entry.id, false)}
                      >
                        <AuthoredPieceThumbnail descriptorId={entry.id} />
                        <b>{displayLabel}</b>
                      </button>
                    ))}
                  </div>
                </section>
              );
            })}
          </div>
          <SelectedPieceCard state={state} runtime={runtime} />
        </div>
      )}
    </div>
  );
}

export function MobileBasicPieceBrowser({
  state,
  runtime,
  catalogMode,
}: BasicBuildProps & { readonly catalogMode: 'regular' | 'special' }) {
  const selectedId = state.authoredSelection.descriptorId;
  const { pitch, bank, selection } = useReconciledBasicSelection(
    state,
    runtime,
    catalogMode === 'regular',
  );
  const specialEntries = SPECIAL_TRACK_GROUPS.flatMap((group) =>
    state.authoredCatalog.filter((entry) => specialTrackGroup(entry.id) === group.id),
  );
  const specials = contextualSpecialTrackEntries(specialEntries);
  const directionScrollerRef = useSelectedOptionScroller(selection.direction);
  const slopeScrollerRef = useSelectedOptionScroller(selection.slope);
  const bankScrollerRef = useSelectedOptionScroller(selection.bank);
  const trackTypeScrollerRef = useSelectedOptionScroller(state.authoredSelection.trackType);
  const specialScrollerRef = useSelectedOptionScroller(selectedId);

  useEffect(() => {
    if (state.preview && state.preview.kind !== 'piece') return;
    if (!isSpecialTrackDescriptor(selectedId)) return;
    const successor = contextualSpecialSuccessor(specialEntries, selectedId);
    const previewId = state.authoredCatalog.find((entry) => entry.selected)?.id ?? null;
    if (successor && successor.id !== previewId) {
      runtime?.selectAuthoredPiece(successor.id, false);
    }
  }, [runtime, selectedId, specialEntries, state.authoredCatalog, state.preview?.kind]);

  const choose = (axis: BasicRegularAxis, choice: BasicRegularChoice): void => {
    const target = resolveBasicRegularPiece(
      state.authoredCatalog,
      selectionWithChoice(selection, axis, choice),
      pitch,
      bank,
    );
    if (target) runtime?.selectAuthoredPiece(target.id, false);
  };
  const available = (axis: BasicRegularAxis, choice: BasicRegularChoice): boolean =>
    basicChoiceAvailable(state.authoredCatalog, selection, axis, choice, pitch, bank);
  const recommended = (axis: BasicRegularAxis, choice: BasicRegularChoice): boolean =>
    resolveBasicRegularPiece(
      state.authoredCatalog,
      selectionWithChoice(selection, axis, choice),
      pitch,
      bank,
    )?.recommendedRank === 1;

  return (
    <div class="mobile-piece-browser">
      {catalogMode === 'regular' ? (
        <div class="mobile-regular-options" data-testid="mobile-regular-options">
          <section class="mobile-option-row mobile-direction-row">
            <h4>Direction</h4>
            <div
              ref={directionScrollerRef}
              class="mobile-option-scroller"
              role="group"
              aria-label="Direction"
            >
              {BASIC_DIRECTION_CHOICES.map((choice) => (
                <button
                  key={choice.id}
                  type="button"
                  aria-label={choice.accessibleLabel}
                  aria-pressed={selection.direction === choice.id}
                  disabled={!available('direction', choice.id)}
                  class={recommended('direction', choice.id) ? 'is-route-recommended' : ''}
                  data-onboarding-target={
                    choice.id === 'direction.left.large'
                      ? 'left-large-turn'
                      : choice.id === 'direction.straight'
                        ? 'direction-straight'
                        : undefined
                  }
                  onClick={() => choose('direction', choice.id)}
                >
                  <DirectionGlyph id={choice.id} />
                  <span>{choice.label}</span>
                </button>
              ))}
            </div>
          </section>

          <section class="mobile-option-row mobile-slope-row">
            <h4>Slope</h4>
            <div
              ref={slopeScrollerRef}
              class="mobile-option-scroller"
              role="group"
              aria-label="Slope"
            >
              {BASIC_SLOPE_CHOICES.map((choice) => (
                <button
                  key={choice.id}
                  type="button"
                  aria-label={`${choice.pitch < 0 ? 'Down ' : choice.pitch > 0 ? 'Up ' : ''}${choice.label}`}
                  aria-pressed={selection.slope === choice.id}
                  disabled={!available('slope', choice.id)}
                  class={recommended('slope', choice.id) ? 'is-route-recommended' : ''}
                  data-onboarding-target={
                    choice.id === 'slope.25'
                      ? 'uphill-slope'
                      : choice.id === 'slope.n25'
                        ? 'downhill-slope'
                        : choice.id === 'slope.0'
                          ? 'level-slope'
                          : undefined
                  }
                  onClick={() => choose('slope', choice.id)}
                >
                  <SlopeGlyph id={choice.id} />
                  <span>{choice.label}</span>
                </button>
              ))}
            </div>
          </section>

          <section class="mobile-option-row mobile-bank-row">
            <h4>Banking</h4>
            <div
              ref={bankScrollerRef}
              class="mobile-option-scroller"
              role="group"
              aria-label="Banking"
            >
              {BASIC_BANK_CHOICES.map((choice) => (
                <button
                  key={choice.id}
                  type="button"
                  aria-pressed={selection.bank === choice.id}
                  disabled={!available('bank', choice.id)}
                  class={recommended('bank', choice.id) ? 'is-route-recommended' : ''}
                  data-onboarding-target={
                    choice.id === 'bank.left'
                      ? 'bank-left'
                      : choice.id === 'bank.level'
                        ? 'bank-level'
                        : undefined
                  }
                  onClick={() => choose('bank', choice.id)}
                >
                  <BankGlyph id={choice.id} />
                  <span>{choice.label}</span>
                </button>
              ))}
            </div>
          </section>

          <section class="mobile-option-row mobile-track-type-row">
            <h4>Track Type</h4>
            <div ref={trackTypeScrollerRef} class="mobile-option-scroller">
              <TrackTypeControl state={state} runtime={runtime} mobile />
            </div>
          </section>
        </div>
      ) : (
        <div
          ref={specialScrollerRef}
          class="mobile-special-browser"
          data-testid="mobile-special-browser"
          aria-label="Special Track choices"
        >
          {specials.map(({ entry, displayLabel }) => (
            <button
              key={entry.id}
              type="button"
              class={`mobile-special-piece ${entry.selected ? 'is-previewing' : ''} ${
                entry.recommendedRank === 1 ? 'is-route-recommended' : ''
              }`}
              aria-pressed={entry.selected}
              data-onboarding-target={entry.id === 'roll.left' ? 'barrel-roll-left' : undefined}
              disabled={!entry.allowed}
              title={entry.allowed ? entry.description : (entry.reason ?? 'Unavailable here')}
              onClick={() => runtime?.selectAuthoredPiece(entry.id, false)}
            >
              <AuthoredPieceThumbnail descriptorId={entry.id} />
              <b>{displayLabel}</b>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
